/**
 * Reduces `data` to at most `maxPoints` entries by splitting it into buckets and
 * keeping the entry holding each series' minimum and maximum within a bucket.
 * Peaks therefore survive the reduction, unlike plain every-nth-entry sampling.
 */
export function downsamplePreservingExtremes<
  TData extends Record<string, unknown>
>(
  data: Array<TData>,
  seriesNames: Array<string>,
  maxPoints: number
): Array<TData> {
  if (data.length <= maxPoints || seriesNames.length === 0) {
    return data;
  }

  const bucketCount = Math.max(
    1,
    Math.floor(maxPoints / (2 * seriesNames.length))
  );
  const bucketSize = Math.ceil(data.length / bucketCount);
  const keptIndices = new Set<number>([0, data.length - 1]);

  for (let start = 0; start < data.length; start += bucketSize) {
    const end = Math.min(start + bucketSize, data.length);

    for (const seriesName of seriesNames) {
      const { minIndex, maxIndex } = getExtremeIndices(
        data,
        seriesName,
        start,
        end
      );

      if (minIndex !== undefined) {
        keptIndices.add(minIndex);
      }
      if (maxIndex !== undefined) {
        keptIndices.add(maxIndex);
      }
    }
  }

  return [...keptIndices].sort((a, b) => a - b).map((index) => data[index]);
}

function getExtremeIndices(
  data: Array<Record<string, unknown>>,
  seriesName: string,
  start: number,
  end: number
) {
  let minIndex: number | undefined;
  let maxIndex: number | undefined;
  let minValue = Number.POSITIVE_INFINITY;
  let maxValue = Number.NEGATIVE_INFINITY;

  for (let index = start; index < end; index++) {
    const value = data[index][seriesName];

    if (typeof value !== "number" || Number.isNaN(value)) {
      continue;
    }
    if (value < minValue) {
      minValue = value;
      minIndex = index;
    }
    if (value > maxValue) {
      maxValue = value;
      maxIndex = index;
    }
  }

  return { minIndex, maxIndex };
}
